import { inject, Injectable } from '@angular/core';
import { ApiDataAccess, Result } from '@softline/core';
import {
  SOFTLINE_API_ITEM_STOCK_AMOUNT,
  SOFTLINE_API_PRODUCT,
  SOFTLINE_API_PRODUCT_IMAGES,
  SOFTLINE_API_PRODUCT_IMAGES_MAIN,
  SOFTLINE_API_PRODUCT_PROMOTIONS,
  SOFTLINE_API_PRODUCT_PURCHASING_PRICES,
  SOFTLINE_API_PRODUCT_SALES_ORGANISATION_STATUSES,
  SOFTLINE_API_PRODUCT_SALES_PRICES,
  SOFTLINE_API_PRODUCT_STORAGE_LOCATIONS,
  SOFTLINE_API_PRODUCT_SUPPLIER_INFORMATION,
  SOFTLINE_API_PRODUCT_SUPPLIER_INFORMATION_MAIN,
  SOFTLINE_API_PRODUCT_UNIT_CONVERSIONS,
  SOFTLINE_API_STOCK_LEVEL,
} from '../product-info.api';
import { PurchasingPriceResponse, SalesPriceResponse } from '../types/prices';
import { ProductResponse } from '../types/product';
import { ProductImageResponse } from '../types/product-image';
import { PromotionEntryResponse } from '../types/promotion';
import { ProductSalesOrganisationStatusResponse } from '../types/sales-organisation-status';
import {
  ProductStockAmountResponse,
  ProductStockStorageLocationResponse,
  StockLevelRequest,
  StockLevelResponse,
} from '../types/stock';
import { SupplierInformationResponse } from '../types/supplier-information';
import { UnitConversionResponse } from '../types/unit-conversion';

@Injectable({ providedIn: 'root' })
export class ProductRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  getById(id: string): Promise<ProductResponse> {
    return this.dataAccess.get<ProductResponse>({
      path: SOFTLINE_API_PRODUCT,
      pathParams: { id },
    });
  }

  getUnitConversions(id: string): Promise<UnitConversionResponse[]> {
    return this.dataAccess.get<UnitConversionResponse[]>({
      path: SOFTLINE_API_PRODUCT_UNIT_CONVERSIONS,
      pathParams: { id },
    });
  }

  getSupplierInformation(id: string): Promise<SupplierInformationResponse[]> {
    return this.dataAccess.get<SupplierInformationResponse[]>({
      path: SOFTLINE_API_PRODUCT_SUPPLIER_INFORMATION,
      pathParams: { id },
    });
  }

  getMainSupplierInformation(id: string): Promise<SupplierInformationResponse> {
    return this.dataAccess.get<SupplierInformationResponse>({
      path: SOFTLINE_API_PRODUCT_SUPPLIER_INFORMATION_MAIN,
      pathParams: { id },
    });
  }

  getStorageLocations(id: string): Promise<ProductStockStorageLocationResponse[]> {
    return this.dataAccess.get<ProductStockStorageLocationResponse[]>({
      path: SOFTLINE_API_PRODUCT_STORAGE_LOCATIONS,
      pathParams: { id },
    });
  }

  getSalesPrices(id: string): Promise<SalesPriceResponse[]> {
    return this.dataAccess.get<SalesPriceResponse[]>({
      path: SOFTLINE_API_PRODUCT_SALES_PRICES,
      pathParams: { id },
    });
  }

  getSalesOrganisationStatuses(
    id: string
  ): Promise<ProductSalesOrganisationStatusResponse[]> {
    return this.dataAccess.get<ProductSalesOrganisationStatusResponse[]>({
      path: SOFTLINE_API_PRODUCT_SALES_ORGANISATION_STATUSES,
      pathParams: { id },
    });
  }

  getPurchasingPrices(id: string): Promise<PurchasingPriceResponse[]> {
    return this.dataAccess.get<PurchasingPriceResponse[]>({
      path: SOFTLINE_API_PRODUCT_PURCHASING_PRICES,
      pathParams: { id },
    });
  }

  getPromotions(id: string): Promise<PromotionEntryResponse[]> {
    return this.dataAccess.get<PromotionEntryResponse[]>({
      path: SOFTLINE_API_PRODUCT_PROMOTIONS,
      pathParams: { id },
    });
  }

  getImages(id: string): Promise<ProductImageResponse[]> {
    return this.dataAccess.get<ProductImageResponse[]>({
      path: SOFTLINE_API_PRODUCT_IMAGES,
      pathParams: { id },
    });
  }

  getMainImage(id: string): Promise<ProductImageResponse> {
    return this.dataAccess.get<ProductImageResponse>({
      path: SOFTLINE_API_PRODUCT_IMAGES_MAIN,
      pathParams: { id },
    });
  }

  /** Lagerstände je Lager. The body selects which extra amounts the backend
      computes — an empty one returns stock + stockLevel only. */
  getStockLevels(
    id: string,
    request: StockLevelRequest = {}
  ): Promise<StockLevelResponse[]> {
    return this.dataAccess.create<StockLevelRequest, StockLevelResponse[]>(
      { path: SOFTLINE_API_STOCK_LEVEL, pathParams: { id } },
      request
    );
  }

  // Unlike the product-service endpoints, the legacy /v1 endpoint wraps its
  // payload in a { result } envelope.
  getStockAmounts(ids: number[]): Promise<ProductStockAmountResponse[]> {
    return this.dataAccess
      .create<{ ids: number[] }, Result<ProductStockAmountResponse[]>>(
        { path: SOFTLINE_API_ITEM_STOCK_AMOUNT },
        { ids }
      )
      .then((response) => response?.result ?? []);
  }
}
